import { ClareStatus, ClareStatusColors } from "@app/shared/model/clare-status.model";
import { DEVICE_CLASSES, PROTOCOL_ADAPTERS } from "@app/shared/services/equator-api/ClareHomeRpc.constants";

import { CHZone } from "@app/area/model/ch-zone";
import { ClareHomeDbCollection } from "@app/shared/resources/resource-objectifier";
import { DbHelper } from "@app/shared/model/db-object.model";
import { IDeviceInfo } from "../services/device-metadata-parser";
import { ImportMap } from "@app/shared/resources/class-importer-exporter.service";

export interface SecurityFobInfo {
    uid: string;
    name: string;
    deviceType: string;
    serialNumber: string;
    zoneNumber: string;
    lowBattery: string;
    id: string;
}

export interface SecurityZoneInfo {
    uid: string;
    name: string;
    deviceType: string;
    serialNumber: string;
    zoneNumber: string;
    lowBattery: string;
    onlineStatus: ClareStatus | [ZoneStatuses, ClareStatusColors];
    signalStrength: number;
    zoneStatus?: [ZoneStatuses, ClareStatusColors];
    id: string;
}

export enum ZoneStatuses {
    alarmed = "Alarmed",
    tampered = "Tampered",
    troubled = "Troubled",
    faulted = "Not Ready",
    ready = "Ready",
    armed = "Armed",
    arming = "Arming",
    armingStay = "Arming Stay",
    armingAway = "Arming Away",
    armedStay = "Armed Stay",
    armedAway = "Armed Away",
    unknown = "Unknown",
}

export interface CHSecurityDevice extends CHDevice {
    Properties: SecurityDeviceProperties;
}

interface SecurityDeviceProperties {
    IMEI: string;
    ICCID: string;
    MEID?: string;
}

export interface DeviceClassObjectProperty {
    Name: string;
    Value: any;
    Polling: number;
    Metadata: { [key: string]: Record<string, unknown> };
}

export interface DeviceClassObjectOperation {
    Name: string;
    Arguments: number;
    Metadata: { [key: string]: Record<string, unknown> };
}

export interface DeviceClassObject {
    DeviceClass: string;
    HomeDeviceUID: string;
    Operations: [DeviceClassObjectOperation];
    Properties: [DeviceClassObjectProperty];
}

export interface DeviceClassObjectSimple {
    DeviceClass: string;
    HomeDeviceUID: string;
    Operations: string[];
    Properties: string[];
}

export interface OutletInfoObject {
    id: string;
    name: string;
    outletNumber: number;
    category?: string;
    type?: string;
}

export interface ChildDeviceInfoObject {
    id: string;
    name: string;
    deviceNumber: number;
    category?: string;
    type?: string;
}

export interface CHDevice {
    Status: number;
    UID: string;
    Name: string;
    Vendor: string;
    Version: string;
    TimestampAdded: number;
    Type: string;
    ParentUID?: string;
    ChildUIDs?: string[];
    ProtocolAdapterName?: string;
    Properties?: Record<string, any>;
    DeviceClasses?: string[];
    DeviceClassObjects?: Record<string, DeviceClassObjectSimple>;
    isFailed?: boolean;

    // Non-standard. Set by other classes.
    zones?: CHZone[];
    info?: IDeviceInfo;
    children?: CHDevice[];

    // Added for zwave config
    masterNode?: string;
}

export class TestingDevice<T> {
    loading = false;
    availability = true;
    strength = 0;
    checked = false;
    icon = "";
    info?: T = null;
    iconError?: boolean;
    deviceUid = "";
    message = "";
}

export class CHDeviceHelper extends DbHelper<CHDevice, CHDevice> {
    importMap: ImportMap<CHDevice, CHDevice> = [
        { i: "Status", o: "Status" },
        { i: "UID", o: "UID" },
        { i: "Name", o: "Name" },
        { i: "Vendor", o: "Vendor" },
        { i: "Version", o: "Version" },
        { i: "TimestampAdded", o: "TimestampAdded" },
        { i: "Type", o: "Type" },
        { i: "ParentUID", o: "ParentUID" },
        { i: "ChildUIDs", o: "ChildUIDs" },
        { i: "ProtocolAdapterName", o: "ProtocolAdapterName" },
        { i: "Properties", o: "Properties" },
        { i: "DeviceClasses", o: "DeviceClasses" },
        { i: "DeviceClassObjects", o: "DeviceClassObjects" },
        { i: "isFailed", o: "isFailed" },
    ];

    static isCameraDevice(device: CHDevice) {
        return device?.ProtocolAdapterName === PROTOCOL_ADAPTERS.CAMERAS;
    }

    static isNvrDevice(device: CHDevice) {
        return this.isCameraDevice(device) && device?.Properties["nvr.channel"];
    }

    static isZWaveDevice(device: CHDevice) {
        return device?.ProtocolAdapterName === PROTOCOL_ADAPTERS.ZWAVE;
    }

    static isClareOneDevice(device: CHDevice) {
        return device?.ProtocolAdapterName === PROTOCOL_ADAPTERS.CLARE_ONE;
    }

    static isHelixPanelDevice(device: CHDevice) {
        return device?.ProtocolAdapterName === PROTOCOL_ADAPTERS.CLARE_SECURE;
    }

    static getDeviceTopParent(device: CHDevice, devices: ClareHomeDbCollection<CHDevice>) {
        if (device?.ParentUID && devices[device.ParentUID]) {
            return this.getDeviceTopParent(devices[device.ParentUID], devices);
        }
        return device;
    }

    static isPeriodicTestSupported(device: CHDevice): boolean {
        const dcos = device?.DeviceClassObjects;
        return dcos?.hasOwnProperty(DEVICE_CLASSES.INTRUSION_CS_TEST_SIGNAL);
    }
}

export enum DEVICE_REAUTH_MODE {
    AUTO = "auto",
    MANUAL = "manual",
}
